import type { MapProvider } from '@gpx-spa/geo-domain';
import type { Ref } from 'vue';
import { onBeforeUnmount, watch } from 'vue';

export type UseMapProviderContainerResizeOptions = {
  /** The DOM node that hosts the map (same element passed to {@link MapProvider.mount}). */
  containerRef: Ref<HTMLElement | null>;
  mapProvider: MapProvider;
  /** Set `true` after a successful mount (e.g. map engine initialized). */
  active: Ref<boolean>;
  observeResize: Ref<boolean>;
  resizeDebounceMs: Ref<number>;
};

/**
 * Observes {@link UseMapProviderContainerResizeOptions.containerRef} and calls
 * {@link MapProvider.notifyContainerResize} when its box changes (flex, sidebars).
 */
export function useMapProviderContainerResize(
  options: UseMapProviderContainerResizeOptions,
): void {
  let observer: ResizeObserver | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  function clearTimer() {
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function notify() {
    options.mapProvider.notifyContainerResize();
  }

  function schedule() {
    clearTimer();
    const ms = options.resizeDebounceMs.value;
    if (ms <= 0) {
      notify();
      return;
    }
    timer = setTimeout(() => {
      timer = null;
      notify();
    }, ms);
  }

  function teardown() {
    observer?.disconnect();
    observer = null;
    clearTimer();
  }

  watch(
    [
      () => options.active.value,
      () => options.containerRef.value,
      () => options.observeResize.value,
    ],
    () => {
      teardown();
      const el = options.containerRef.value;
      if (!options.active.value || !el || !options.observeResize.value) {
        return;
      }
      observer = new ResizeObserver(() => {
        schedule();
      });
      observer.observe(el);
      schedule();
    },
    { flush: 'post' },
  );

  onBeforeUnmount(() => {
    teardown();
  });
}
